import { useEffect, useMemo, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import {
  draftDocumentHasContent,
  fromProseMirrorDoc,
  parseDraftDocumentModel,
  toProseMirrorDoc,
} from "@/lib/draft-document-utils";
import { cn } from "@/lib/utils";
import type { DraftDocumentModel, DraftMatterDetail } from "@/types/drafting";

const DraftSectionHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      sectionId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-section-id"),
        renderHTML: (attributes) =>
          attributes.sectionId ? { "data-section-id": attributes.sectionId as string } : {},
      },
      sectionKey: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-section-key"),
        renderHTML: (attributes) =>
          attributes.sectionKey ? { "data-section-key": attributes.sectionKey as string } : {},
      },
    };
  },
});

interface DraftEditorProps {
  contentJson: unknown;
  contentVersionKey?: string | null;
  matter: DraftMatterDetail;
  onChange: (model: DraftDocumentModel, html: string) => void;
  readOnly?: boolean;
}

export function DraftEditor({
  contentJson,
  contentVersionKey,
  matter,
  onChange,
  readOnly = false,
}: DraftEditorProps) {
  const modelRef = useRef(parseDraftDocumentModel(contentJson));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSyncedContentRef = useRef<string | null>(null);
  const suppressChangeRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      DraftSectionHeading.configure({ levels: [1, 2, 3] }),
    ],
    editable: !readOnly,
    content: toProseMirrorDoc(modelRef.current),
    onUpdate: ({ editor: ed }) => {
      if (readOnly || suppressChangeRef.current) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const json = ed.getJSON();
        const nextModel = fromProseMirrorDoc(json, modelRef.current);
        modelRef.current = nextModel;
        onChange(nextModel, ed.getHTML());
      }, 600);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const syncKey = `${contentVersionKey ?? ""}:${JSON.stringify(contentJson)}`;
    if (syncKey === lastSyncedContentRef.current) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const nextModel = parseDraftDocumentModel(contentJson);
    const previousModel = modelRef.current;
    const currentHasContent = draftDocumentHasContent(previousModel);
    if (!draftDocumentHasContent(nextModel) && currentHasContent) {
      lastSyncedContentRef.current = syncKey;
      return;
    }

    const previousJson = editor.getJSON();
    const pmDoc = toProseMirrorDoc(nextModel);
    modelRef.current = nextModel;
    suppressChangeRef.current = true;
    editor.commands.setContent(pmDoc, { emitUpdate: false });
    if (!editor.getText().trim() && draftDocumentHasContent(nextModel)) {
      editor.commands.setContent(pmDoc, { emitUpdate: false });
    }
    if (!editor.getText().trim() && currentHasContent) {
      editor.commands.setContent(previousJson, { emitUpdate: false });
      modelRef.current = previousModel;
    }
    suppressChangeRef.current = false;
    lastSyncedContentRef.current = syncKey;
  }, [contentJson, contentVersionKey, editor]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const documentTitle = useMemo(() => {
    const model = parseDraftDocumentModel(contentJson);
    return model.title || matter.title || "Untitled Draft";
  }, [contentJson, matter.title]);
  const roleLabel = matter.legal_role_name ?? "Assigned counsel";
  const jurisdictionLabel = matter.jurisdiction ?? "Not specified";

  if (!editor) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-xl border border-[var(--lm-border-subtle)] bg-white dark:border-slate-700 dark:bg-slate-900/40">
        <p className="text-sm text-[var(--lm-muted)]">Loading editor...</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--lm-border-subtle)] bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/40">
      <div className="border-b border-[var(--lm-border-subtle)] px-6 py-2.5 text-center dark:border-slate-700">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-400/90">
          Draft — Working Version
        </p>
      </div>

      <div className="border-b border-[var(--lm-border-subtle)] px-6 py-4 text-center dark:border-slate-700">
        <h2 className="text-lg font-semibold text-[var(--lm-foreground-strong)] dark:text-white sm:text-xl">
          {documentTitle}
        </h2>
        <p className="mt-1.5 text-xs text-[var(--lm-muted)] dark:text-slate-400 sm:text-sm">
          Governing Law: {jurisdictionLabel}
          <span className="mx-2 text-[var(--lm-border-subtle)] dark:text-slate-600">·</span>
          Assigned Counsel: {roleLabel}
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[var(--lm-border-subtle)] px-3 py-2 dark:border-slate-700">
        <EditorToolbarButton
          label="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={readOnly}
        />
        <EditorToolbarButton
          label="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={readOnly}
        />
        <EditorToolbarButton
          label="H1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          disabled={readOnly}
        />
        <EditorToolbarButton
          label="H2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          disabled={readOnly}
        />
        <EditorToolbarButton
          label="Bullet"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={readOnly}
        />
        <EditorToolbarButton
          label="Numbered"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={readOnly}
        />
      </div>

      <EditorContent
        editor={editor}
        className={cn(
          "draft-document-editor prose prose-sm max-w-none px-6 py-4 dark:prose-invert",
          "min-h-[360px] focus:outline-none",
          "[&_.ProseMirror]:min-h-[320px] [&_.ProseMirror]:outline-none"
        )}
      />
    </div>
  );
}

function EditorToolbarButton({
  label,
  active,
  onClick,
  disabled,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded px-2 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-[var(--surface-sunken)] text-[var(--lm-muted)] hover:bg-[var(--lm-hover)] dark:bg-slate-800 dark:text-slate-300"
      )}
    >
      {label}
    </button>
  );
}
